/**
 * UpsList — Listado de SAI (Sistemas de Alimentación Ininterrumpida).
 *
 * Sigue el patrón común de listados del CMDB (ver RoutersList / FirewallsList):
 * filtros arriba, tabla, export a Excel y ficha a pantalla completa.
 * Cubre tanto SAIs de **rack** como de **torre**.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { BoltIcon } from '@heroicons/react/24/outline';
import UpsForm from './UpsForm';
import useNavOrder from '../hooks/useNavOrder';
import { useAutoOpen } from '../hooks/useAutoOpen';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import ExportExcelButton from './ExportExcelButton';

const STATUS_BADGE = {
  active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  standby:     { bg: '#e0f2fe', color: '#0369a1', label: 'Standby' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
};

const FORM_FACTOR_BADGE = {
  rack:  { bg: '#ede9fe', color: '#5b21b6', icon: '🗄️', label: 'Rack' },
  tower: { bg: '#e0f2fe', color: '#0369a1', icon: '🏢', label: 'Torre' },
};

function fmtDate(value) {
  if (!value) return '';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('es-ES');
  } catch {
    return value;
  }
}

function fmtPower(ups) {
  const parts = [];
  if (ups.capacity_va) parts.push(`${ups.capacity_va} VA`);
  if (ups.capacity_w) parts.push(`${ups.capacity_w} W`);
  return parts.join(' · ');
}

export default function UpsList({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // null = lista, {} = nuevo, ups = editar
  const [search, setSearch] = useState('');
  const [filterForm, setFilterForm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Los filtros nacen plegados: el buscador se queda siempre a la vista.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hlId, setHlId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.getUpsList(companyId);
      setItems(res.data || []);
    } catch (e) {
      console.error('UpsList load error', e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Resalta el SAI indicado por ?destacar=<id> (desde documentación / buscador)
  // y con `&abrir=1` abre además su ficha.
  useEffect(() => {
    if (loading || !items.length) return;
    const params = new URLSearchParams(window.location.search);
    const d = Number(params.get('destacar'));
    if (!d) return;
    if (params.get('abrir') === '1') {
      const target = items.find((u) => Number(u.id) === d);
      if (target) { setSelected(target); return; }
    }
    setHlId(d);
    const t1 = setTimeout(() => {
      const el = document.getElementById(`ups-row-${d}`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 120);
    const t2 = setTimeout(() => setHlId(null), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading]);

  const handleClose = () => setSelected(null);
  // Si el formulario acaba de crear el SAI, el listado lo adopta como seleccionado:
  // la ficha se queda abierta en modo edición en vez de cerrarse.
  const handleFormSave = (creado) => {
    load();
    if (creado?.id) setSelected(creado);
  };
  useAutoOpen(items, setSelected);

  const filtered = items.filter(u => {
    if (filterForm && u.form_factor !== filterForm) return false;
    if (filterStatus && u.status !== filterStatus) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(s) ||
      u.vendor?.toLowerCase().includes(s) ||
      u.model?.toLowerCase().includes(s) ||
      u.serial_number?.toLowerCase().includes(s) ||
      u.asset_tag?.toLowerCase().includes(s) ||
      u.management_ip?.toLowerCase().includes(s) ||
      u.location_name?.toLowerCase().includes(s) ||
      u.rack_name?.toLowerCase().includes(s)
    );
  });

  const exportRows = useMemo(() => filtered.map(u => {
    const badge = STATUS_BADGE[u.status] || STATUS_BADGE.active;
    return {
      'Nombre': u.name,
      'Fabricante': u.vendor || '',
      'Modelo': u.model || '',
      'Nº de serie': u.serial_number || '',
      'Asset tag': u.asset_tag || '',
      'Formato': FORM_FACTOR_BADGE[u.form_factor]?.label || u.form_factor || '',
      'Tecnología': u.technology_label || u.technology || '',
      'Potencia (VA)': u.capacity_va ?? '',
      'Potencia (W)': u.capacity_w ?? '',
      'Carga actual (W)': u.current_load_w ?? '',
      'Baterías': u.battery_quantity ?? '',
      'Autonomía (min)': u.battery_runtime_min ?? '',
      'Próxima sustitución baterías': fmtDate(u.battery_replace_date),
      'Ubicación': u.location_name || '',
      'Rack': u.rack_name || '',
      'Posición (U)': u.position_u ?? '',
      'IP de gestión': u.management_ip || '',
      'Fecha de compra': fmtDate(u.purchase_date),
      'Fin de garantía': fmtDate(u.warranty_end),
      'Estado': badge.label,
    };
  }), [filtered]);

  // ── Ficha ────────────────────────────────────────────────────────────────
  const navUps = useNavOrder(filtered, selected, selected !== null);
  if (selected !== null) {
    return (
      <UpsForm
        ups={selected.id ? selected : null}
        companyId={companyId}
        variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
        navItems={navUps}
        navIndex={selected.id ? navUps.findIndex(u => String(u.id) === String(selected.id)) : -1}
        onNavigate={(destino) => setSelected(destino)}
        onClose={handleClose}
        onSave={handleFormSave}
      />
    );
  }

  // ── Listado ──────────────────────────────────────────────────────────────
  return (
    <div className="wol-container">
      <div className="wol-header">
        <div className="wol-header-left">
          <BoltIcon className="wol-icon" />
          <div>
            <h1 className="wol-title">SAI (UPS)</h1>
            <p className="wol-subtitle">
              {items.length} registros · {filtered.length} filtrados
            </p>
          </div>
        </div>
        <button
          type="button"
          className="wol-btn-secondary"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          Filtros
          {[filterForm, filterStatus].filter(Boolean).length > 0
            ? ` (${[filterForm, filterStatus].filter(Boolean).length})`
            : ''}
        </button>
        <ExportExcelButton rows={exportRows} fileBase="sai" sheetName="SAI" />
        <button className="wol-btn-primary" onClick={() => setSelected({})}>
          ＋ Nuevo SAI
        </button>
      </div>

      <div className="wol-filters">
        <input
          className="wol-search"
          placeholder="Buscar por nombre, marca, modelo, serie, IP…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {filtersOpen && (
          <>
            <select className="wol-select" value={filterForm} onChange={e => setFilterForm(e.target.value)}>
              <option value="">Todos los formatos</option>
              <option value="rack">🗄️ Rack</option>
              <option value="tower">🏢 Torre</option>
            </select>
            <select className="wol-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="standby">Standby</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="decom">Decommissioned</option>
            </select>
          </>
        )}
      </div>

      {loading && (
        <div className="wol-loading">
          <div className="spinner-border text-primary" />
          <span>Cargando</span>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="wol-empty">
          <span>🔋</span>
          <p className="empty-title">No hay SAI todavía</p>
          <p className="empty-subtitle">
            Registra los SAI de torre y de rack para inventariar potencia, baterías y garantías.
          </p>
          <button className="wol-btn-primary" onClick={() => setSelected({})}>
            ＋ Crear el primer SAI
          </button>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="wol-table-wrap">
          <table className="wol-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Marca / Modelo</th>
                <th>Formato</th>
                <th>Potencia</th>
                <th>Batería</th>
                <th>Tomas</th>
                <th>Ubicación / Rack</th>
                <th>IP gestión</th>
                <th>Compra</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const badge = STATUS_BADGE[u.status] || STATUS_BADGE.active;
                const ff = FORM_FACTOR_BADGE[u.form_factor] || FORM_FACTOR_BADGE.rack;
                return (
                  <tr
                    key={u.id}
                    id={`ups-row-${u.id}`}
                    onClick={() => setSelected(u)}
                    style={{
                      cursor: 'pointer',
                      ...(u.id === hlId
                        ? { outline: '2px solid #f59e0b', outlineOffset: -2, background: 'rgba(245,158,11,.10)' }
                        : {}),
                    }}
                  >
                    <td>
                      <span style={{ marginRight: 6 }}>🔋</span>
                      <strong>{u.name}</strong>
                      {u.serial_number && (
                        <span style={{ marginLeft: 6, fontSize: '0.78rem', color: '#64748b' }}>
                          ({u.serial_number})
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{u.vendor || '—'}</span>
                      {u.model && (
                        <span style={{ marginLeft: 4, fontSize: '0.8rem', color: '#64748b' }}>
                          · {u.model}
                        </span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        background: ff.bg, color: ff.color, borderRadius: 4,
                        padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {ff.icon} {ff.label}
                      </span>
                    </td>
                    <td>
                      {fmtPower(u) ? (
                        <span style={{ fontSize: '0.82rem', color: '#334155' }}>{fmtPower(u)}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {u.battery_quantity || u.battery_runtime_min ? (
                        <div style={{ lineHeight: 1.4 }}>
                          {u.battery_quantity > 0 && (
                            <span style={{ fontSize: '0.8rem', color: '#334155' }}>
                              {u.battery_quantity} uds.
                            </span>
                          )}
                          {u.battery_runtime_min > 0 && (
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>
                              ⏱ {u.battery_runtime_min} min
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {u.outlets_total > 0 ? (
                        <span style={{ fontSize: '0.8rem', color: '#334155' }}>
                          <strong>{u.outlets_used || 0}</strong> / {u.outlets_total}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {u.location_name ? (
                        <div style={{ lineHeight: 1.4 }}>
                          <span style={{ fontSize: '0.82rem', color: '#334155' }}>{u.location_name}</span>
                          {u.rack_name && (
                            <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>
                              🗄 {u.rack_name}{u.position_u ? ` · U${u.position_u}` : ''}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {u.management_ip ? (
                        <span style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: '#334155' }}>
                          {u.management_ip}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {u.purchase_date ? (
                        <span style={{ fontSize: '0.8rem', color: '#334155' }}>{fmtDate(u.purchase_date)}</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        background: badge.bg, color: badge.color, borderRadius: 4,
                        padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
